export const RESPONSIBILITY_SOURCES = ['self', 'supervised-teams', 'led-department', 'planned-teams', 'tenant'] as const;
export type ResponsibilitySource = (typeof RESPONSIBILITY_SOURCES)[number];

export type ManagedPermissionScope = 'scoped' | 'full';

export const BUSINESS_GROUP_KEYS = ['employee', 'supervisor', 'chief', 'planner', 'hr'] as const;
export type BusinessGroupKey = (typeof BUSINESS_GROUP_KEYS)[number];

export interface ManagedDefaultRole {
  groupKey: BusinessGroupKey;
  groupName: string;
  roleId: string;
  description: string;
  responsibilitySource: ResponsibilitySource;
  permissions: Readonly<Record<string, ManagedPermissionScope>>;
}

export const TECHNICAL_ADMIN_GROUP = { key: 'admin', name: 'Administrators', roleId: 'admin' } as const;

const S = 'scoped';
const F = 'full';

// ADR-0022 only accepts `scoped` on employees/teams/calendars/departments; other grants are on/off (`full`)
// and their rows are narrowed by the consuming service through the caller's scoped read family (ADR-0023).
const EMPLOYEE: ManagedDefaultRole = {
  groupKey: 'employee',
  groupName: 'Employee',
  roleId: 'default-employee',
  description: 'Self-service: own calendar, clocking, day confirmation, absence requests, operational teammate view.',
  responsibilitySource: 'self',
  permissions: {
    'users:profile:read': F,
    'users:profile:update': F,
    'users:security:update': F,
    'users:accounts:read': F,
    'users:accounts:update': F,
    'dashboard:read': F,
    'dashboard:employee:read': F,
    'schedules:read:own': F,
    'schedules:read:team': F,
    'shift-swaps:read': F,
    'shift-swaps:request': F,
    'shift-applications:read': F,
    'shift-applications:apply': F,
    'requests:read': F,
    'requests:create': F,
    'calendars:read': S,
    'calendars:update': S,
    'timetracking:my-week:read': F,
    'timetracking:my-week:confirm': F,
    'timetracking:blocked-window:book': F,
    'teams:read': S,
    'teams:details:read': S,
    'employees:read': S,
    'notifications:read': F,
    'notifications:manage': F,
    'documents:read:own': F,
    'documents:upload:own': F,
  },
};

const SUPERVISOR: ManagedDefaultRole = {
  groupKey: 'supervisor',
  groupName: 'Supervisor',
  roleId: 'default-supervisor',
  description: 'Operational supervision, time review and absence decisions within supervised teams.',
  responsibilitySource: 'supervised-teams',
  permissions: {
    'users:profile:read': F,
    'users:profile:update': F,
    'users:security:update': F,
    'users:accounts:read': F,
    'users:accounts:update': F,
    'dashboard:read': F,
    'dashboard:planning:read': F,
    'reports:generate': F,
    'schedules:read:own': F,
    'schedules:read:team': F,
    'requests:read': F,
    'requests:approve': F,
    'calendars:read': S,
    'calendars:update': S,
    'teams:read': S,
    'teams:details:read': S,
    'employees:read': S,
    'employees:teams:read': S,
    'employees:skills:read': S,
    'employees:balances:read': S,
    'plans:read': F,
    'plan-instances:read': F,
    'shifts:read': F,
    'shift-units:read': F,
    'strategies:read': F,
    'labour-pool:read': F,
    'labour-pool:create': F,
    'labour-pool:manage': F,
    'timetracking:overview:read': F,
    'timetracking:overview:confirm': F,
    'timetracking:overview:update': F,
    'timetracking:review-queue:read': F,
    'timetracking:review-queue:approve': F,
    'timetracking:absences:read': F,
    'timetracking:absences:update': F,
    'timetracking:absences:approve': F,
    'timetracking:manual-bookings:read': F,
    'timetracking:manual-bookings:update': F,
    'timetracking:blocked-window:book': F,
    'planning:shift-overrides:approve': F,
    'approvals:read': F,
    'approvals:decide': F,
    'addresses:read': F,
    'notifications:read': F,
    'notifications:manage': F,
  },
};

const CHIEF: ManagedDefaultRole = {
  groupKey: 'chief',
  groupName: 'Chief',
  roleId: 'default-chief',
  description: 'Team and person management plus basic reporting within the led department.',
  responsibilitySource: 'led-department',
  permissions: {
    'users:profile:read': F,
    'users:profile:update': F,
    'users:security:update': F,
    'users:accounts:read': F,
    'users:accounts:update': F,
    'dashboard:read': F,
    'dashboard:planning:read': F,
    'reports:generate': F,
    'schedules:read:own': F,
    'schedules:read:team': F,
    'calendars:read': S,
    'departments:read': S,
    'teams:read': S,
    'teams:create': S,
    'teams:update': S,
    'teams:delete': S,
    'teams:details:read': S,
    'teams:details:update': S,
    'teams:appearance:read': S,
    'teams:appearance:update': S,
    'employees:read': S,
    'employees:create': S,
    'employees:update': S,
    'employees:delete': S,
    'employees:address:read': S,
    'employees:address:update': S,
    'employees:contacts:read': S,
    'employees:contacts:update': S,
    'employees:skills:read': S,
    'employees:skills:update': S,
    'employees:teams:read': S,
    'employees:teams:update': S,
    'employees:balances:read': S,
    'timetracking:blocked-window:book': F,
    'addresses:read': F,
    'notifications:read': F,
    'notifications:manage': F,
  },
};

const PLANNER: ManagedDefaultRole = {
  groupKey: 'planner',
  groupName: 'Planner',
  roleId: 'default-planner',
  description: 'Plan, assign shifts and publish schedules within coordinated teams.',
  responsibilitySource: 'planned-teams',
  permissions: {
    'users:profile:read': F,
    'users:profile:update': F,
    'users:security:update': F,
    'users:accounts:read': F,
    'users:accounts:update': F,
    'dashboard:read': F,
    'dashboard:planning:read': F,
    'reports:generate': F,
    'schedules:read:own': F,
    'schedules:read:team': F,
    'requests:read': F,
    'requests:approve': F,
    'calendars:read': S,
    'calendars:update': S,
    'teams:read': S,
    'teams:details:read': S,
    'employees:read': S,
    'employees:teams:read': S,
    'employees:skills:read': S,
    'employees:update': S,
    'teams:update': S,
    'plans:read': F,
    'plans:create': F,
    'plans:update': F,
    'plan-instances:read': F,
    'plan-instances:create': F,
    'plan-instances:update': F,
    'shifts:read': F,
    'shifts:create': F,
    'shifts:update': F,
    'shift-units:read': F,
    'shift-units:create': F,
    'shift-units:update': F,
    'strategies:read': F,
    'labour-pool:read': F,
    'labour-pool:create': F,
    'labour-pool:manage': F,
    'locations:read': F,
    'skills:read': F,
    'settings:read': F,
    'timetracking:periods:read': F,
    'timetracking:periods:update': F,
    'timetracking:blocked-window:book': F,
    'planning:shift-overrides:approve': F,
    'approvals:read': F,
    'approvals:decide': F,
    'addresses:read': F,
    'notifications:read': F,
    'notifications:manage': F,
  },
};

const HR: ManagedDefaultRole = {
  groupKey: 'hr',
  groupName: 'HR',
  roleId: 'default-hr',
  description: 'Tenant-wide person data, time management, month close and basic reporting. Not a technical administrator.',
  responsibilitySource: 'tenant',
  permissions: {
    'users:profile:read': F,
    'users:profile:update': F,
    'users:security:update': F,
    'users:accounts:read': F,
    'users:accounts:update': F,
    'dashboard:read': F,
    'dashboard:planning:read': F,
    'dashboard:stats:read': F,
    'reports:generate': F,
    'reports:export': F,
    'schedules:read:own': F,
    'schedules:read:team': F,
    'calendars:read': F,
    'calendars:update': F,
    'departments:read': F,
    'teams:read': F,
    'teams:details:read': F,
    'employees:read': F,
    'employees:create': F,
    'employees:update': F,
    'employees:delete': F,
    'employees:address:read': F,
    'employees:address:update': F,
    'employees:contacts:read': F,
    'employees:contacts:update': F,
    'employees:skills:read': F,
    'employees:skills:update': F,
    'employees:teams:read': F,
    'employees:teams:update': F,
    'employees:balances:read': F,
    'employees:balances:update': F,
    'plans:read': F,
    'plans:update': F,
    'labour-pool:read': F,
    'labour-pool:create': F,
    'labour-pool:manage': F,
    'plan-instances:read': F,
    'plan-instances:update': F,
    'timetracking:overview:read': F,
    'timetracking:overview:confirm': F,
    'timetracking:overview:update': F,
    'timetracking:review-queue:read': F,
    'timetracking:review-queue:approve': F,
    'timetracking:review-queue:read-all': F,
    'timetracking:absences:read': F,
    'timetracking:absences:update': F,
    'timetracking:absences:approve': F,
    'timetracking:persons:read': F,
    'timetracking:persons:update': F,
    'timetracking:periods:read': F,
    'timetracking:manual-bookings:read': F,
    'timetracking:manual-bookings:update': F,
    'timetracking:month-close:read': F,
    'timetracking:month-close:update': F,
    'timetracking:entitlements:update': F,
    'timetracking:blocked-window:book': F,
    'approvals:read': F,
    'approvals:decide': F,
    'addresses:read': F,
    'notifications:read': F,
    'notifications:manage': F,
  },
};

export const MANAGED_DEFAULT_ROLES: readonly ManagedDefaultRole[] = [EMPLOYEE, SUPERVISOR, CHIEF, PLANNER, HR];

export const DEFAULT_ONBOARDING_GROUP_KEY: BusinessGroupKey = 'employee';
